/**
 * OverviewDashboard — Admin Operational Overview dashboard (ODE-P07-T01).
 *
 * Renders the server-derived operational overview (REQ-043 live positions,
 * REQ-044 DO status, REQ-048 compact recap) with the full REQ-090 state set:
 *
 *   loading       snapshot fetch in flight
 *   ready         server snapshot rendered (KPI + positions + DO + recap)
 *   empty         explicit "no operational data" note (nothing fabricated)
 *   stale         per-row + summary warnings when a fix is older than the
 *                 server-published `staleAfterMs` bound
 *   disconnected  live feed dropped — the last snapshot stays visible but is
 *                 explicitly labeled as not current (never presented as live)
 *   unauthorized  401 — session missing/expired
 *   blocked       403 — role denied (server-enforced; the mirror is UX-only)
 *   error         503/network — retryable via the explicit reconnect loop
 *
 * Every value rendered here comes from the server snapshot — the dashboard
 * never computes its own freshness/staleness bound, never invents positions
 * or statuses. All output carries stable `data-testid`s for deterministic
 * tests, and status is never encoded by color alone (text markers).
 *
 * Traces to: ODE-PRD-REQ-043, ODE-PRD-REQ-044, ODE-PRD-REQ-048,
 *            ODE-PRD-REQ-090.
 */

import { useAuth } from '../../auth/AuthContext.js';
import type { OverviewFetchResult, OverviewSnapshot } from '../../api/client.js';
import { StateView } from '../../components/StateView.js';
import type { ModuleState } from '../state.js';
import { useOverviewLiveFeed } from './useOverviewLiveFeed.js';
import type { WebSocketLike } from './live-feed.js';

/** Test seams (mirrors the hook's injectable seams). */
export interface OverviewDashboardSeams {
  readonly createSocket?: (url: string) => WebSocketLike | null;
  readonly fetchSnapshot?: (token: string) => Promise<OverviewFetchResult>;
  readonly streamUrl?: (token: string) => string;
}

/** Formats an epoch-ms instant for display (deterministic ISO string). */
export function formatEpochMs(epochMs: number): string {
  return new Date(epochMs).toISOString();
}

/** Plain-text freshness label (status is never color-only). */
export function formatFreshness(freshnessMs: number): string {
  const seconds = Math.max(0, Math.round(freshnessMs / 1000));
  return `${seconds} dtk lalu`;
}

export function OverviewDashboard({
  seams = {},
}: {
  readonly seams?: OverviewDashboardSeams;
} = {}): JSX.Element {
  const { session } = useAuth();
  const { data, feed, reload } = useOverviewLiveFeed({
    token: session?.token ?? null,
    createSocket: seams.createSocket,
    fetchSnapshot: seams.fetchSnapshot,
    streamUrl: seams.streamUrl,
  });

  return (
    <section aria-labelledby="overview-title" className="module">
      <h2 id="overview-title">Operational Overview</h2>
      <p className="module-description">
        Ikhtisar operasional: KPI, posisi driver, status Delivery Order, dan rekap
        ringkas — dihitung di server (REQ-043/044/048).
      </p>

      {data.kind === 'ready' && (
        <p
          data-testid={
            feed === 'live'
              ? 'overview-feed-live'
              : feed === 'connecting'
                ? 'overview-feed-connecting'
                : 'overview-feed-disconnected-chip'
          }
          className="overview-feed-chip"
          role="status"
        >
          {feed === 'live'
            ? 'Umpan langsung aktif.'
            : feed === 'connecting'
              ? 'Menghubungkan umpan langsung…'
              : 'Umpan langsung terputus.'}
        </p>
      )}

      {data.kind === 'ready' && feed === 'disconnected' && (
        <div
          role="alert"
          data-testid="overview-feed-disconnected"
          className="state state-blocked overview-banner"
        >
          <h3>Umpan langsung terputus</h3>
          <p>
            Koneksi live terputus — data di bawah adalah snapshot terakhir yang
            diketahui, bukan kondisi terkini.
          </p>
          <button type="button" data-testid="overview-reconnect" onClick={reload}>
            Sambungkan ulang
          </button>
        </div>
      )}

      {data.kind !== 'ready' ? (
        <StateView state={toModuleState(data)} onRetry={reload} />
      ) : (
        <OverviewBody snapshot={data.snapshot} />
      )}
    </section>
  );
}

/** Maps the overview data state to the shared REQ-090 state renderer. */
function toModuleState(data: ReturnType<typeof useOverviewLiveFeed>['data']): ModuleState {
  switch (data.kind) {
    case 'loading':
      return { kind: 'loading' };
    case 'ready':
      // Unreachable (guarded by the caller); keeps the mapping exhaustive.
      return { kind: 'empty', payload: null };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: data.message };
    case 'error':
      return { kind: 'error', message: data.message };
  }
}

function OverviewBody({ snapshot }: { readonly snapshot: OverviewSnapshot }): JSX.Element {
  const positions = snapshot.livePositions;
  const doStatus = snapshot.doStatus;
  const isEmpty = positions.length === 0 && doStatus.length === 0;

  return (
    <div data-testid="overview-ready">
      {snapshot.kpi.stalePositions > 0 && (
        <div
          role="status"
          data-testid="overview-stale-warning"
          className="state state-unauthorized overview-banner"
        >
          <h3>Data posisi usang</h3>
          <p>
            {snapshot.kpi.stalePositions} posisi lebih lama dari ambang{' '}
            {Math.round(snapshot.staleAfterMs / 1000)} dtk — mungkin bukan kondisi
            terkini.
          </p>
        </div>
      )}

      {isEmpty && (
        <p data-testid="overview-empty" className="overview-empty">
          Belum ada data operasional untuk ditampilkan.
        </p>
      )}

      <section aria-label="KPI" data-testid="kpi-summary" className="overview-section">
        <h3>KPI</h3>
        <dl className="kpi-grid">
          <KpiCard testId="kpi-active-sessions" label="Sesi aktif" value={snapshot.kpi.activeSessions} />
          <KpiCard testId="kpi-active-drivers" label="Driver aktif" value={snapshot.kpi.activeDrivers} />
          <KpiCard testId="kpi-total-samples" label="Total sampel" value={snapshot.kpi.totalSamples} />
          <KpiCard testId="kpi-open-gaps" label="Gap terbuka" value={snapshot.kpi.openGaps} />
          <KpiCard testId="kpi-stale-positions" label="Posisi usang" value={snapshot.kpi.stalePositions} />
          {Object.entries(snapshot.kpi.doStatusCounts).map(([status, count]) => (
            <KpiCard
              key={status}
              testId={`kpi-do-${status}`}
              label={`DO ${status}`}
              value={count}
            />
          ))}
        </dl>
      </section>

      <section aria-label="Posisi driver" data-testid="live-positions" className="overview-section">
        <h3>Posisi driver terbaru (REQ-043)</h3>
        {positions.length === 0 ? (
          <p data-testid="positions-empty">Tidak ada posisi live saat ini.</p>
        ) : (
          <table className="overview-table">
            <thead>
              <tr>
                <th scope="col">Driver</th>
                <th scope="col">Delivery Order</th>
                <th scope="col">Koordinat</th>
                <th scope="col">Akurasi</th>
                <th scope="col">Kebaruan</th>
              </tr>
            </thead>
            <tbody>
              {positions.map((row) => {
                const stale = row.freshnessMs > snapshot.staleAfterMs;
                return (
                  <tr
                    key={row.trackingSessionId}
                    data-testid={`position-${row.trackingSessionId}`}
                    className={stale ? 'position-row stale' : 'position-row'}
                  >
                    <td>{row.driverId}</td>
                    <td>{row.deliveryOrderId}</td>
                    <td>
                      {row.latitude.toFixed(5)}, {row.longitude.toFixed(5)}
                    </td>
                    <td>{row.accuracyMeters === null ? '—' : `${row.accuracyMeters} m`}</td>
                    <td>
                      {formatFreshness(row.freshnessMs)}{' '}
                      <span
                        data-testid={`freshness-${row.trackingSessionId}`}
                        className={stale ? 'badge badge-stale' : 'badge badge-fresh'}
                      >
                        {stale ? 'Usang' : 'Segar'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section aria-label="Status Delivery Order" data-testid="do-status" className="overview-section">
        <h3>Status Delivery Order (REQ-044)</h3>
        {doStatus.length === 0 ? (
          <p data-testid="do-status-empty">Tidak ada Delivery Order.</p>
        ) : (
          <table className="overview-table">
            <thead>
              <tr>
                <th scope="col">Delivery Order</th>
                <th scope="col">Status</th>
                <th scope="col">Sesi aktif</th>
              </tr>
            </thead>
            <tbody>
              {doStatus.map((row) => (
                <tr key={row.deliveryOrderId} data-testid={`do-${row.deliveryOrderId}`}>
                  <td>{row.deliveryOrderId}</td>
                  <td>{row.status}</td>
                  <td>{row.hasActiveSession ? 'Ya' : 'Tidak'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section aria-label="Rekap operasional" data-testid="recap" className="overview-section">
        <h3>Rekap operasional (REQ-048)</h3>
        <dl className="kpi-grid">
          <KpiCard testId="recap-total-dos" label="Total DO" value={snapshot.recap.totalDeliveryOrders} />
          <KpiCard testId="recap-in-flight-dos" label="DO dalam proses" value={snapshot.recap.inFlightDeliveryOrders} />
          <KpiCard testId="recap-completed-dos" label="DO selesai" value={snapshot.recap.completedDeliveryOrders} />
          <KpiCard testId="recap-cancelled-dos" label="DO dibatalkan" value={snapshot.recap.cancelledDeliveryOrders} />
        </dl>
        <p className="overview-asof" data-testid="overview-asof">
          Snapshot per {formatEpochMs(snapshot.asOfEpochMs)}
        </p>
      </section>
    </div>
  );
}

function KpiCard({
  testId,
  label,
  value,
}: {
  readonly testId: string;
  readonly label: string;
  readonly value: number;
}): JSX.Element {
  return (
    <div className="kpi-card" data-testid={testId}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
